import {
  type WorkplaceProfileResponse,
  workplaceProfileResponseSchema,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type z } from "zod";

interface GetWorkplaceParams {
  workplaceId?: string;
  include?: Array<"accountManager">;
}

const baseUrl = `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces`;

export function getWorkplaceQueryKey(workplaceId: string, include?: Array<"accountManager">) {
  const queryParams = new URLSearchParams();
  if (Array.isArray(include) && include.length > 0) {
    queryParams.set("include", include.join(","));
  }

  const queryString = queryParams.toString();
  const fullQueryString = queryString ? `?${queryString}` : "";
  return `${baseUrl}/${workplaceId}${fullQueryString}` as const;
}

export function useGetWorkplace(
  params: GetWorkplaceParams,
  options: UseGetQueryOptions<WorkplaceProfileResponse> & {
    customHeaders?: { Authorization: string };
  } = {}
) {
  const { workplaceId, include } = params;

  const url = getWorkplaceQueryKey(workplaceId ?? "", include);

  return useGetQuery({
    url,
    responseSchema: workplaceProfileResponseSchema as z.ZodType<WorkplaceProfileResponse>,
    meta: { logErrorMessage: APP_V2_APP_EVENTS.GET_WORKPLACE_FAILURE },
    enabled: isDefined(workplaceId),
    useErrorBoundary: false,
    retry: (failureCount, error) =>
      failureCount < 3 && isDefined(error.response) && error.response.status >= 500,
    ...options,
  });
}
